import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Share2, Eye, Play, Pause, Volume2, VolumeX, Plus, ChevronUp, ChevronDown, Send, X, Trash2, TrendingUp, Clock, Megaphone, Video as VideoIcon, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";
import { ReportButton } from "@/components/ReportButton";
import WeeklyContestBanner from "@/components/WeeklyContestBanner";

export default function VideoFeed() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [activeTab, setActiveTab] = useState("latest"); // latest or trending
  const containerRef = useRef(null);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setCurrentIndex(0);
    setShowComments(false);
    try {
      const endpoint = activeTab === "trending" ? `${API}/videos/trending` : `${API}/videos`;
      const response = await fetch(endpoint, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
      }
    } catch (error) {
      toast.error("Failed to load videos");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const likeVideo = async (videoId) => {
    try {
      const response = await fetch(`${API}/videos/${videoId}/like`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        const { liked, count } = await response.json();
        setVideos(videos.map(v =>
          v.video_id === videoId
            ? { ...v, likes: liked ? [...(v.likes || []), user.user_id] : (v.likes || []).filter(id => id !== user.user_id) }
            : v
        ));
      }
    } catch (error) {
      // Non-critical: like state will refresh on next fetch
    }
  };

  const addComment = async (videoId) => {
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    try {
      const response = await fetch(`${API}/videos/${videoId}/comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: commentText.trim() }),
      });
      if (response.ok) {
        const comment = await response.json();
        setVideos(videos.map(v =>
          v.video_id === videoId
            ? { ...v, comments: [...(v.comments || []), comment] }
            : v
        ));
        setCommentText("");
      }
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const trackView = async (videoId) => {
    try {
      await fetch(`${API}/videos/${videoId}/view`, { method: "POST", credentials: "include" });
    } catch (error) { /* View tracking is fire-and-forget */ }
  };

  const deleteVideo = async (videoId) => {
    try {
      const response = await fetch(`${API}/videos/${videoId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        setVideos(videos.filter(v => v.video_id !== videoId));
        toast.success("Video deleted");
      }
    } catch (error) {
      toast.error("Failed to delete video");
    }
  };

  const goToVideo = (direction) => {
    if (direction === "up" && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowComments(false);
    } else if (direction === "down" && currentIndex < videos.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowComments(false);
    }
  };

  const currentVideo = videos[currentIndex];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #000022 0%, #000044 100%)" }}>
      <Header />

      <main className="flex-1 container mx-auto px-4 py-6 max-w-2xl">
        {/* This Week's Contest banner (auto-hides if no active contest) */}
        <WeeklyContestBanner />

        {/* Upload Button */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold glitter-text" data-testid="video-feed-title">Video Feed</h1>
          <button
            onClick={() => setShowUpload(true)}
            className="myspace-btn-primary flex items-center gap-2"
            data-testid="upload-video-btn"
          >
            <Plus className="w-4 h-4" />
            Upload
          </button>
        </div>

        {/* Feed Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("latest")}
            className={`flex items-center gap-2 px-4 py-2 font-bold text-sm transition-all ${
              activeTab === "latest"
                ? "bg-gradient-to-r from-[#FF00FF] to-[#CC00CC] text-white border-2 border-[#FF00FF]"
                : "myspace-btn"
            }`}
            style={activeTab === "latest" ? { boxShadow: "0 0 15px #FF00FF", fontFamily: "'Pixelify Sans', sans-serif", textTransform: "uppercase" } : {}}
            data-testid="tab-latest"
          >
            <Clock className="w-4 h-4" />
            Latest
          </button>
          <button
            onClick={() => setActiveTab("trending")}
            className={`flex items-center gap-2 px-4 py-2 font-bold text-sm transition-all ${
              activeTab === "trending"
                ? "bg-gradient-to-r from-[#FF6600] to-[#FF3300] text-white border-2 border-[#FF6600]"
                : "myspace-btn"
            }`}
            style={activeTab === "trending" ? { boxShadow: "0 0 15px #FF6600", fontFamily: "'Pixelify Sans', sans-serif", textTransform: "uppercase" } : {}}
            data-testid="tab-trending"
          >
            <TrendingUp className="w-4 h-4" />
            Trending
          </button>
        </div>

        {/* First-time hint: Promote your video */}
        {videos.some(v => v.user_id === user?.user_id) && !localStorage.getItem("ourspace_promote_hint_dismissed") && (
          <div
            className="myspace-card mb-4 flex items-start gap-3 border-[#FF6600]"
            style={{ background: "rgba(255,102,0,0.08)" }}
            data-testid="promote-hint-banner"
          >
            <Megaphone className="w-5 h-5 text-[#FF6600] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-[#FF6600]">💡 Promote your videos!</p>
              <p className="text-xs text-[#99CCFF] mt-0.5">
                Click the orange megaphone on your videos to feature them at the top of everyone's feed for 24h–7 days.
              </p>
            </div>
            <button
              onClick={() => { localStorage.setItem("ourspace_promote_hint_dismissed", "1"); setLoading(l => l); window.location.reload(); }}
              className="text-[#FF66FF] hover:text-[#FF6600] text-xs"
              data-testid="dismiss-promote-hint"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#FF00FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#FF66FF]">Loading videos...</p>
            </div>
          </div>
        ) : videos.length === 0 ? (
          <div className="myspace-card text-center py-16" data-testid="no-videos">
            <Play className="w-16 h-16 mx-auto mb-4 text-[#FF00FF]" />
            <h2 className="text-2xl font-bold text-[#FF66FF] mb-2">No Videos Yet</h2>
            <p className="text-[#99CCFF] mb-6">Be the first to upload a video!</p>
            <button
              onClick={() => setShowUpload(true)}
              className="myspace-btn-primary"
              data-testid="upload-first-video-btn"
            >
              Upload Video
            </button>
          </div>
        ) : (
          <div className="relative" ref={containerRef}>
            {/* Video Player Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentVideo?.video_id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                className="myspace-card relative"
                data-testid={`video-card-${currentVideo?.video_id}`}
              >
                {/* Video Player */}
                <VideoPlayer
                  video={currentVideo}
                  onView={() => trackView(currentVideo.video_id)}
                />

                {/* Video Info */}
                <div className="mt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-[#00FFFF]" style={{ textShadow: "0 0 10px #00FFFF" }}>
                        {currentVideo?.title}
                        {activeTab === "trending" && (
                          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gradient-to-r from-[#FF6600] to-[#FF3300] text-white border border-[#FF6600]" style={{ boxShadow: "0 0 8px #FF6600" }}>
                            <TrendingUp className="w-3 h-3" />
                            HOT
                          </span>
                        )}
                      </h3>
                      <div
                        className="flex items-center gap-2 mt-1 cursor-pointer"
                        onClick={() => navigate(`/profile/${currentVideo?.user_id}`)}
                      >
                        {currentVideo?.author_picture ? (
                          <img src={currentVideo.author_picture} alt="" className="w-6 h-6 border border-[#FF00FF] object-cover" />
                        ) : (
                          <div className="w-6 h-6 border border-[#FF00FF] bg-[#330066] flex items-center justify-center text-xs text-[#FF66FF]">
                            {currentVideo?.author_name?.[0]}
                          </div>
                        )}
                        <span className="text-sm text-[#FF66FF] hover:text-[#00FFFF]">
                          {currentVideo?.author_name}
                        </span>
                      </div>
                      {currentVideo?.description && (
                        <p className="text-sm text-[#99CCFF] mt-2">{currentVideo.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Tags */}
                  {currentVideo?.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {currentVideo.tags.map((tag, i) => (
                        <span key={`tag-${tag}-${i}`} className="interest-tag">#{tag}</span>
                      ))}
                    </div>
                  )}

                  {/* Actions Bar */}
                  <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#6600CC]">
                    <button
                      onClick={() => likeVideo(currentVideo.video_id)}
                      className={`flex items-center gap-1.5 transition-all ${
                        currentVideo?.likes?.includes(user?.user_id)
                          ? "text-[#FF0066]"
                          : "text-[#FF66FF] hover:text-[#FF0066]"
                      }`}
                      data-testid={`like-video-${currentVideo?.video_id}`}
                    >
                      <Heart className="w-5 h-5" fill={currentVideo?.likes?.includes(user?.user_id) ? "currentColor" : "none"} />
                      <span className="text-sm font-bold">{currentVideo?.likes?.length || 0}</span>
                    </button>

                    <button
                      onClick={() => setShowComments(!showComments)}
                      className="flex items-center gap-1.5 text-[#FF66FF] hover:text-[#00FFFF] transition-all"
                      data-testid={`comments-toggle-${currentVideo?.video_id}`}
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span className="text-sm font-bold">{currentVideo?.comments?.length || 0}</span>
                    </button>

                    <div className="flex items-center gap-1.5 text-[#6699CC]">
                      <Eye className="w-5 h-5" />
                      <span className="text-sm">{currentVideo?.views || 0}</span>
                    </div>

                    {/* Duet — anyone can duet someone else's video */}
                    {currentVideo?.user_id !== user?.user_id && (
                      <button
                        onClick={() => navigate(`/duet/${currentVideo.video_id}`)}
                        className="flex items-center gap-1.5 text-[#00FF99] hover:text-[#00FFFF] transition-all"
                        data-testid={`duet-video-${currentVideo?.video_id}`}
                        title="Record a Duet"
                      >
                        <VideoIcon className="w-5 h-5" />
                      </button>
                    )}

                    {/* Promote — only own videos */}
                    {currentVideo?.user_id === user?.user_id && !currentVideo?.is_sponsored && (
                      <button
                        onClick={() => navigate(`/promote/${currentVideo.video_id}`)}
                        className="flex items-center gap-1.5 text-[#FF6600] hover:text-[#FFD700] transition-all"
                        data-testid={`promote-video-${currentVideo?.video_id}`}
                        title="Promote this video"
                      >
                        <Megaphone className="w-5 h-5" />
                      </button>
                    )}

                    {/* Sponsored badge */}
                    {currentVideo?.is_sponsored && (
                      <span
                        className="flex items-center gap-1 text-xs font-bold text-[#FF6600] border border-[#FF6600] px-2 py-0.5 rounded"
                        data-testid={`sponsored-badge-${currentVideo?.video_id}`}
                        title="This video is sponsored"
                      >
                        <Sparkles className="w-3 h-3" /> SPONSORED
                      </span>
                    )}

                    {currentVideo?.user_id === user?.user_id && (
                      <button
                        onClick={() => deleteVideo(currentVideo.video_id)}
                        className="ml-auto text-[#FF0000] hover:text-[#FF6666] transition-all"
                        data-testid={`delete-video-${currentVideo?.video_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}

                    {/* Report — only on others' videos */}
                    {currentVideo && currentVideo.user_id !== user?.user_id && (
                      <div className={currentVideo?.user_id === user?.user_id ? "" : "ml-auto"}>
                        <ReportButton
                          targetType="video"
                          targetId={currentVideo.video_id}
                          reportedUserId={currentVideo.user_id}
                          iconOnly
                          size="sm"
                          variant="ghost"
                          className="border-none text-[#FF6666] hover:text-[#FF0000]"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Comments Panel */}
                <AnimatePresence>
                  {showComments && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 pt-3 border-t border-[#6600CC]">
                        <h4 className="section-header">Comments</h4>
                        <div className="max-h-48 overflow-y-auto space-y-2 mb-3">
                          {(currentVideo?.comments || []).length === 0 ? (
                            <p className="text-sm text-[#6699CC] text-center py-4">No comments yet. Be the first!</p>
                          ) : (
                            currentVideo.comments.map((c, i) => (
                              <div key={c.comment_id || i} className="comment-box">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="comment-author text-sm">{c.author_name}</span>
                                  <span className="comment-date">
                                    {new Date(c.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="text-sm text-[#CCCCFF]">{c.content}</p>
                              </div>
                            ))
                          )}
                        </div>
                        {/* Comment Input */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder="Add a comment..."
                            className="myspace-input flex-1 text-sm"
                            onKeyDown={(e) => e.key === "Enter" && addComment(currentVideo.video_id)}
                            data-testid="video-comment-input"
                          />
                          <button
                            onClick={() => addComment(currentVideo.video_id)}
                            disabled={!commentText.trim() || submittingComment}
                            className="myspace-btn px-3 disabled:opacity-50"
                            data-testid="video-comment-submit"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <div className="flex items-center justify-center gap-4 mt-4">
              <button
                onClick={() => goToVideo("up")}
                disabled={currentIndex === 0}
                className="myspace-btn px-3 py-2 disabled:opacity-30"
                data-testid="video-nav-prev"
              >
                <ChevronUp className="w-5 h-5" />
              </button>
              <span className="text-[#FF66FF] font-mono text-sm">
                {currentIndex + 1} / {videos.length}
              </span>
              <button
                onClick={() => goToVideo("down")}
                disabled={currentIndex === videos.length - 1}
                className="myspace-btn px-3 py-2 disabled:opacity-30"
                data-testid="video-nav-next"
              >
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUpload && (
          <VideoUploadModal
            onClose={() => setShowUpload(false)}
            onSuccess={() => {
              setShowUpload(false);
              fetchVideos();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ========== Video Player Component ========== */
function VideoPlayer({ video, onView }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [viewTracked, setViewTracked] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setViewTracked(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [video?.video_id]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
      if (!viewTracked) {
        onView?.();
        setViewTracked(true);
      }
    }
    setPlaying(!playing);
  };

  if (!video?.video_url) {
    return (
      <div className="aspect-[9/16] max-h-[60vh] bg-black flex items-center justify-center border-2 border-[#6600CC]">
        <p className="text-[#6699CC]">Video unavailable</p>
      </div>
    );
  }

  return (
    <div className="relative aspect-[9/16] max-h-[60vh] bg-black border-2 border-[#6600CC] overflow-hidden group cursor-pointer" onClick={togglePlay}>
      <video
        ref={videoRef}
        src={video.video_url}
        className="w-full h-full object-contain"
        loop
        muted={muted}
        playsInline
        preload="metadata"
        data-testid="video-player"
      />

      {/* Play/Pause Overlay */}
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <div className="w-16 h-16 border-2 border-[#FF00FF] flex items-center justify-center bg-[rgba(0,0,51,0.8)]" style={{ boxShadow: "0 0 20px #FF00FF" }}>
            <Play className="w-8 h-8 text-[#FF00FF]" />
          </div>
        </div>
      )}

      {/* Mute Toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
        className="absolute bottom-3 right-3 p-2 bg-[rgba(0,0,51,0.8)] border border-[#FF00FF] text-[#FF66FF] hover:text-[#00FFFF] transition-all opacity-0 group-hover:opacity-100"
        data-testid="video-mute-toggle"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
    </div>
  );
}

/* ========== Video Upload Modal ========== */
function VideoUploadModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState("select"); // select, details, uploading
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const allowedTypes = ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Invalid file type. Please upload MP4, WebM, MOV, or AVI.");
      return;
    }
    if (selectedFile.size > 100 * 1024 * 1024) {
      toast.error("File too large. Maximum 100MB.");
      return;
    }

    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
    setStep("details");
  };

  const handleUpload = async () => {
    if (!file || !title.trim()) return;
    setUploading(true);
    setStep("uploading");

    try {
      // Step 1: Create video post
      const createRes = await fetch(`${API}/videos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          tags: tags.split(",").map(t => t.trim()).filter(Boolean),
        }),
      });

      if (!createRes.ok) throw new Error("Failed to create video post");
      const videoPost = await createRes.json();

      // Step 2: Upload video file
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await fetch(`${API}/upload/video`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!uploadRes.ok) throw new Error("Failed to upload video file");
      const uploadData = await uploadRes.json();

      // Step 3: Attach media URL to video post
      const videoUrl = `${API}/files/${uploadData.file_id}`;
      await fetch(`${API}/videos/${videoPost.video_id}/media`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ video_url: videoUrl }),
      });

      toast.success("Video uploaded successfully!");
      onSuccess();
    } catch (error) {
      toast.error(error.message || "Upload failed");
      setStep("details");
    } finally {
      setUploading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="myspace-card w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="video-upload-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#00FFFF]" style={{ textShadow: "0 0 10px #00FFFF" }}>
            Upload Video
          </h2>
          <button onClick={onClose} className="myspace-btn px-2 py-1" data-testid="close-upload-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === "select" && (
          <div className="text-center py-8">
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="video-file-input"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-[#FF00FF] p-12 cursor-pointer hover:border-[#00FFFF] hover:bg-[rgba(0,255,255,0.05)] transition-all"
            >
              <Play className="w-12 h-12 mx-auto mb-4 text-[#FF00FF]" />
              <p className="text-[#FF66FF] font-bold mb-2">Click to select a video</p>
              <p className="text-sm text-[#6699CC]">MP4, WebM, MOV, AVI - Max 100MB</p>
            </div>
          </div>
        )}

        {step === "details" && (
          <div className="space-y-4">
            {/* Video Preview */}
            {preview && (
              <div className="aspect-video bg-black border-2 border-[#6600CC] overflow-hidden">
                <video src={preview} className="w-full h-full object-contain" controls muted />
              </div>
            )}

            <div>
              <label className="block text-sm text-[#FF66FF] mb-1 font-bold">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your video a title..."
                className="myspace-input"
                data-testid="video-title-input"
              />
            </div>

            <div>
              <label className="block text-sm text-[#FF66FF] mb-1 font-bold">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Tell people about this video..."
                className="myspace-input min-h-[80px] resize-none"
                data-testid="video-description-input"
              />
            </div>

            <div>
              <label className="block text-sm text-[#FF66FF] mb-1 font-bold">Tags (comma separated)</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="music, dance, comedy..."
                className="myspace-input"
                data-testid="video-tags-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => { setStep("select"); setFile(null); setPreview(null); }} className="myspace-btn flex-1">
                Back
              </button>
              <button
                onClick={handleUpload}
                disabled={!title.trim()}
                className="myspace-btn-primary flex-1 disabled:opacity-50"
                data-testid="video-submit-btn"
              >
                Upload Video
              </button>
            </div>
          </div>
        )}

        {step === "uploading" && (
          <div className="text-center py-12">
            <div className="w-20 h-20 border-4 border-[#00FFFF] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <p className="text-[#00FFFF] font-bold text-lg mb-2">Uploading your video...</p>
            <p className="text-[#6699CC] text-sm">This may take a moment depending on file size</p>
            <div className="mt-4 w-full bg-[#000033] border border-[#6600CC] h-3">
              <div className="h-full bg-gradient-to-r from-[#FF00FF] to-[#00FFFF] transition-all" style={{ width: uploading ? "70%" : "100%" }} />
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
